import { forkJoin, Observable, of } from 'rxjs';
import { Store } from '@ngrx/store';
import { clone } from 'ramda';
import { map, take } from 'rxjs/operators';
import { MeetConstants } from '../meet/meet.constants';
import { AnalyticsService } from '../core/analytics';
import * as fromRoot from '../reducers';
import { CustomerDisplay, CustomersNameService } from './customer-names.service';

export abstract class AdditionalFilterCustomersBase {
  public id;
  public filterTitle;
  public page;
  public loading$: Observable<boolean>;
  public error$: Observable<string>;
  public customerFilter$: Observable<any[]>;
  public customerIds$: Observable<string[]>;
  public customerNames: CustomerDisplay[];
  public allCustomerNames: CustomerDisplay[];
  public allCustomerIds: string[] = [];
  public filteredCustomerNames: CustomerDisplay[] = [];
  public totalCustomerCount = 0;
  public pageSize = 20;
  public currentPage = 0;
  public lastNonSearchPage = 0;
  public customerSelections: any[];
  public columns: any[];
  public customerNamesLoading = true;
  public searchTerm;
  public placeHolderText = 'Search By Name';
  protected readonly searchDebounceMs = 250;
  protected searchDebounceHandle: any;
  protected searchRequestId = 0;
  protected pageRequestId = 0;
  protected customerNamesCache: Map<string, CustomerDisplay> = new Map();
  protected selectedSavedGroupId: string;
  protected readonly MeetConstants = MeetConstants;

  protected constructor(
    protected readonly store: Store<fromRoot.State>,
    protected readonly analytics: AnalyticsService,
    protected readonly customersNameService: CustomersNameService,
  ) {}

  protected initializeColumns() {
    this.columns = [{
      prop: 'fullName',
      title: 'Name',
      sort: false,
      size: 175
    }];
  }

  public updateCustomerNames() {
    switch (this.id) {
      case MeetConstants.includeFilterId: {
        this.customerFilter$ = this.store.select(fromRoot.getIncludedCustomersByPage(this.page));
        this.customerFilter$.subscribe(customers => {
          this.updateCustomerIdsAndLoadCurrentPage(customers);
        });
        break;
      }
      case MeetConstants.excludeFilterId: {
        this.customerFilter$ = this.store.select(fromRoot.getExcludedCustomersByPage(this.page));
        this.customerFilter$.subscribe(customers => {
          this.updateCustomerIdsAndLoadCurrentPage(customers);
        });
        break;
      }
      case MeetConstants.savedGroupsFilterId: {
        this.loading$ = this.store.select(fromRoot.getSelectedSavedGroupCustomersLoading);
        this.error$ = this.store.select(fromRoot.getSelectedSavedGroupCustomersError);
        this.customerIds$ = this.store.select(fromRoot.getSelectedSavedGroupAllCustomerIds);
        this.loading$.subscribe(loading => {
          if (!loading) {
            this.customerIds$.pipe(take(1)).subscribe(ids => {
              this.updateCustomerIdsAndLoadCurrentPage(ids);
            });
          }
        });
        this.store.select(fromRoot.getSelectedSavedGroupId).pipe(take(1)).subscribe(id => {
          this.selectedSavedGroupId = id;
        });

        break;
      }
    }
  }

  public search(immediate = false) {
    const normalizedSearchTerm = (this.searchTerm ?? '').toString().trim();
    if (!normalizedSearchTerm) {
      this.clearSearch();
      return;
    }

    if (this.searchDebounceHandle) {
      clearTimeout(this.searchDebounceHandle);
      this.searchDebounceHandle = null;
    }

    if (immediate) {
      this.executeSearch(normalizedSearchTerm);
      return;
    }

    this.searchDebounceHandle = setTimeout(() => {
      this.executeSearch(normalizedSearchTerm);
      this.searchDebounceHandle = null;
    }, this.searchDebounceMs);
  }

  protected executeSearch(normalizedSearchTerm: string) {
    const requestId = ++this.searchRequestId;
    this.pageRequestId++;

    if (!this.isSearchApplied()) {
      this.lastNonSearchPage = this.currentPage;
    }

    this.searchTerm = normalizedSearchTerm;
    this.customerNamesLoading = true;
    this.currentPage = 0;

    this.loadAllCustomerNamesForSearch().subscribe({
      next: (customers: CustomerDisplay[]) => {
        if (requestId !== this.searchRequestId || normalizedSearchTerm !== this.searchTerm) {
          return;
        }

        this.filteredCustomerNames = customers
          .filter((customer: CustomerDisplay) => customer.fullName.toUpperCase().includes(normalizedSearchTerm.toUpperCase()));
        this.customerNames = this.getPageRows(this.filteredCustomerNames, this.currentPage);
        this.customerNamesLoading = false;
      },
      error: () => {
        if (requestId !== this.searchRequestId || normalizedSearchTerm !== this.searchTerm) {
          return;
        }

        this.filteredCustomerNames = [];
        this.customerNames = [];
        this.customerNamesLoading = false;
      }
    });
  }

  public clearSearch() {
    if (this.searchDebounceHandle) {
      clearTimeout(this.searchDebounceHandle);
      this.searchDebounceHandle = null;
    }

    this.searchRequestId++;
    this.searchTerm = '';
    this.filteredCustomerNames = [];
    this.currentPage = this.lastNonSearchPage;
    this.customerNamesLoading = true;
    this.loadCustomersForCurrentPage();
  }

  public onSelection(sel) {
    this.customerSelections = sel;
  }

  public onAllSelected(selected) {
    if (selected) {
      this.customerSelections = clone(this.customerNames).map(customer => customer.id);
    } else {
      this.customerSelections = [];
    }
  }

  public getNumberOfPages(total: number) {
    return Math.ceil(total / this.pageSize);
  }

  public getDisplayedTotalCount(): number {
    if (this.isSearchApplied()) {
      return this.filteredCustomerNames?.length ?? 0;
    }
    return this.totalCustomerCount;
  }

  public getDisplayedNumberOfPages(): number {
    return this.getNumberOfPages(this.getDisplayedTotalCount());
  }

  public getDisplayedCurrentPage(): number {
    return this.currentPage;
  }

  public onCustomerPageChange(page: number) {
    this.currentPage = page;
    if (this.isSearchApplied()) {
      this.customerNames = this.getPageRows(this.filteredCustomerNames, this.currentPage);
      return;
    }
    this.customerNamesLoading = true;
    this.loadCustomersForCurrentPage();
  }

  protected getSelectedCustomers(): CustomerDisplay[] {
    return clone(this.customerNames).filter(customer =>
      this.customerSelections.includes(customer.id)
    );
  }

  protected formatCustomerDisplayNames(customers: any[]): CustomerDisplay[] {
    return (customers ?? []).map((customer: any) => {
      const firstName = (customer.firstName ?? '').toString().trim();
      const lastName = (customer.lastName ?? '').toString().trim();
      const fullName = (customer.fullName ?? '').toString().trim();
      const customerName = (customer.name ?? '').toString().trim();
      const id = (customer.id ?? customer.cfaID ?? '').toString().trim();

      const fallbackFullName = fullName
        || customerName
        || [firstName, lastName].filter(Boolean).join(' ');

      return {
        ...customer,
        id,
        firstName,
        lastName,
        fullName: fallbackFullName,
      };
    });
  }

  protected updateCustomerIdsAndLoadCurrentPage(customersOrIds: any[]) {
    this.allCustomerIds = this.extractCustomerIds(customersOrIds);
    this.totalCustomerCount = this.allCustomerIds.length;

    const validIds = new Set(this.allCustomerIds);
    this.customerNamesCache.forEach((_, id) => {
      if (!validIds.has(id)) {
        this.customerNamesCache.delete(id);
      }
    });

    const maxPage = this.totalCustomerCount > 0 ? this.getNumberOfPages(this.totalCustomerCount) - 1 : 0;
    this.currentPage = Math.min(this.currentPage, maxPage);

    if (this.isSearchApplied()) {
      this.search(true);
      return;
    }

    this.loadCustomersForCurrentPage();
  }

  protected loadCustomersForCurrentPage() {
    const requestId = ++this.pageRequestId;
    const pageIds = this.getCurrentPageCustomerIds();

    if (pageIds.length === 0) {
      this.allCustomerNames = [];
      this.customerNames = [];
      this.customerNamesLoading = false;
      return;
    }

    this.customersNameService.populateCustomerNames(pageIds).subscribe({
      next: (names: CustomerDisplay[]) => {
        if (requestId !== this.pageRequestId || this.isSearchApplied()) {
          return;
        }

        const formattedCustomers = this.formatCustomerDisplayNames(names);
        formattedCustomers.forEach((customer: CustomerDisplay) => this.customerNamesCache.set(customer.id, customer));
        this.allCustomerNames = formattedCustomers;
        this.customerNames = formattedCustomers;
        this.customerNamesLoading = false;
      },
      error: () => {
        if (requestId !== this.pageRequestId || this.isSearchApplied()) {
          return;
        }

        this.allCustomerNames = this.getCachedCustomers(pageIds);
        this.customerNames = this.allCustomerNames;
        this.customerNamesLoading = false;
      }
    });
  }

  protected getCurrentPageCustomerIds(): string[] {
    const startIndex = this.currentPage * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    return this.allCustomerIds.slice(startIndex, endIndex);
  }

  protected extractCustomerIds(customersOrIds: any[]): string[] {
    return (customersOrIds ?? [])
      .map((customerOrId: any) => {
        if (typeof customerOrId === 'string') {
          return customerOrId;
        }
        return customerOrId?.id ?? customerOrId?.cfaID ?? '';
      })
      .map((id: any) => (id ?? '').toString().trim())
      .filter((id: string) => id.length > 0);
  }

  protected isSearchApplied(): boolean {
    return !!(this.searchTerm && this.searchTerm.toString().trim().length > 0);
  }

  protected loadAllCustomerNamesForSearch(): Observable<CustomerDisplay[]> {
    const missingIds = this.allCustomerIds.filter((id: string) => !this.customerNamesCache.has(id));

    if (missingIds.length === 0) {
      return of(this.getCachedCustomers(this.allCustomerIds));
    }

    const requests = this.chunkIds(missingIds, this.pageSize)
      .map((ids: string[]) => this.customersNameService.populateCustomerNames(ids));

    return forkJoin(requests).pipe(
      map((customerChunks: CustomerDisplay[][]) => {
        const flattenedCustomers = customerChunks.reduce((all: CustomerDisplay[], chunk: CustomerDisplay[]) => {
          return all.concat(chunk);
        }, []);
        const formattedCustomers = this.formatCustomerDisplayNames(flattenedCustomers);
        formattedCustomers.forEach((customer: CustomerDisplay) => this.customerNamesCache.set(customer.id, customer));
        return this.getCachedCustomers(this.allCustomerIds);
      })
    );
  }

  protected getCachedCustomers(ids: string[]): CustomerDisplay[] {
    return ids
      .map((id: string) => this.customerNamesCache.get(id))
      .filter((customer: CustomerDisplay | undefined): customer is CustomerDisplay => !!customer);
  }

  protected chunkIds(ids: string[], size: number): string[][] {
    const chunks: string[][] = [];
    for (let index = 0; index < ids.length; index += size) {
      chunks.push(ids.slice(index, index + size));
    }
    return chunks;
  }

  protected getPageRows(customers: CustomerDisplay[], page: number): CustomerDisplay[] {
    const startIndex = page * this.pageSize;
    return customers.slice(startIndex, startIndex + this.pageSize);
  }
}

